import React, { useState } from 'react';
import { X } from 'lucide-react';

import { Button } from '../../ui/button';
import { Input } from '../../ui/input';

const MAX_ITEMS = 500;

/**
 * Reusable chip input for filter configuration.
 *
 * Controlled component: parent owns `items` and provides `onAdd` / `onRemove` /
 * `onBulkAdd`. Enter key or Add button submits. Duplicates (case-insensitive)
 * are ignored. Enforces a 500-item limit matching the backend Pydantic
 * `max_length=500` on `FilterConfig.lexical_words` and `email_addresses`.
 *
 * Bulk paste: when `onBulkAdd` is provided, a "Bulk paste" toggle reveals a
 * textarea that splits on commas, newlines, or semicolons and adds all entries
 * at once (respecting the 500 limit).
 *
 * Used by InboxEditorModal for email addresses and lexical words.
 */
export default function FilterChipInput({
    items = [],
    onAdd,
    onRemove,
    onBulkAdd,
    placeholder = '',
    testIdPrefix = 'filter',
}) {
    const [value, setValue] = useState('');
    const [showBulkPaste, setShowBulkPaste] = useState(false);
    const [bulkText, setBulkText] = useState('');
    const atLimit = items.length >= MAX_ITEMS;

    const handleAdd = () => {
        if (atLimit) return;
        const trimmed = value.trim().toLowerCase();
        if (!trimmed) return;
        if (items.some(item => item.toLowerCase() === trimmed)) {
            setValue('');
            return;
        }
        onAdd(trimmed);
        setValue('');
    };

    const handleKeyDown = e => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAdd();
        }
    };

    const handleBulkPaste = () => {
        if (!onBulkAdd) return;
        const entries = bulkText
            .split(/[,;\n\r]+/)
            .map(s => s.trim().toLowerCase())
            .filter(s => s && !items.includes(s))
            .slice(0, MAX_ITEMS - items.length);
        if (entries.length > 0) {
            onBulkAdd(entries);
        }
        setBulkText('');
        setShowBulkPaste(false);
    };

    return (
        <div>
            <div className="flex gap-2">
                <Input
                    value={value}
                    onChange={e => setValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={atLimit ? `Limit reached (${MAX_ITEMS})` : placeholder}
                    className="flex-1"
                    disabled={atLimit}
                    data-testid={`${testIdPrefix}-input`}
                />
                <Button
                    variant="outline"
                    size="sm"
                    onClick={handleAdd}
                    disabled={!value.trim() || atLimit}
                    data-testid={`${testIdPrefix}-add`}
                >
                    Add
                </Button>
                {onBulkAdd && !atLimit && (
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowBulkPaste(!showBulkPaste)}
                        data-testid={`${testIdPrefix}-bulk-toggle`}
                    >
                        Bulk paste
                    </Button>
                )}
            </div>
            {atLimit && (
                <p className="text-xs text-amber-600 mt-1" data-testid={`${testIdPrefix}-limit-warning`}>
                    Maximum {MAX_ITEMS} entries reached. Remove items to add more.
                </p>
            )}
            {showBulkPaste && (
                <div className="mt-2">
                    <textarea
                        value={bulkText}
                        onChange={e => setBulkText(e.target.value)}
                        placeholder="Paste comma or newline separated words..."
                        className="w-full text-sm border rounded p-2 min-h-24"
                        data-testid={`${testIdPrefix}-bulk-input`}
                    />
                    <Button
                        variant="outline"
                        size="sm"
                        className="mt-1"
                        onClick={handleBulkPaste}
                        disabled={!bulkText.trim()}
                        data-testid={`${testIdPrefix}-bulk-add`}
                    >
                        Add all
                    </Button>
                </div>
            )}
            {items.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                    <span className="text-xs text-stone-400" data-testid={`${testIdPrefix}-count`}>
                        {items.length}/{MAX_ITEMS}
                    </span>
                    {items.map((item, idx) => (
                        <span
                            key={`${item}-${idx}`}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-stone-100 border border-stone-200 rounded-full text-xs text-stone-700"
                            data-testid={`${testIdPrefix}-chip-${idx}`}
                        >
                            {item}
                            <button
                                type="button"
                                onClick={() => onRemove(idx)}
                                className="text-stone-400 hover:text-red-500"
                                data-testid={`${testIdPrefix}-remove-${idx}`}
                            >
                                <X className="w-3 h-3" />
                            </button>
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}
